import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';
import { usePipelineProgress } from '@/hooks/usePipelineProgress';

export interface EntityRole {
  role_type: string;
  side_alignment: string;
  role_description: string | null;
}

export interface EntityRelationship {
  relationship_type: string;
  target_entity_id: string;
  target_entity_name: string;
}

export interface CanonicalEntity {
  id: string;
  /** Display name: the most frequently observed name across documents. */
  name: string;
  /** Stored canonical name, which may differ from the display name. */
  canonical_name: string;
  entity_type: 'person' | 'organization' | 'other';
  entity_kind: string;
  description: string | null;
  /** Display aliases: stored aliases plus the canonical name when it differs. */
  aliases: string[];
  /** Raw stored aliases, safe to edit and write back. */
  stored_aliases: string[];
  roles: EntityRole[];
  relationships: EntityRelationship[];
  document_guids: string[];
  /** Observations of this entity across in-scope documents. */
  mention_count: number;
  /** Overlay merge sources folded into this survivor (empty when none). */
  merged_source_ids?: string[];
}

interface EntitiesResponse {
  entities: CanonicalEntity[];
}

/** Idle poll so an open entities page sees cleanup watermarks without consolidation. */
const ENTITIES_PROGRESS_POLL_MS = 60_000;

export function useEntities(matterGuid: string | undefined) {
  const { progress, mutate: mutateProgress } = usePipelineProgress(matterGuid);
  const stamp = progress
    ? `${progress.stages.entities_consolidated.done}:${progress.stages.entities_consolidated.total}:${progress.processing}:${progress.entities_updated_at ?? ''}:${progress.entities_cleanup_at ?? ''}`
    : null;

  const { data, error, mutate, isLoading } = useSWR<EntitiesResponse>(
    matterGuid ? `/entities/${matterGuid}` : null,
    async (url: string) => {
      const response = await backendClient.get(url);
      return { entities: response.data.entities || [] };
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  );

  // Pipeline progress stops polling when the matter is idle; keep a slow tick
  // while this hook is mounted so cleanup overlays can refresh the list.
  useEffect(() => {
    if (!matterGuid) return;
    const timer = setInterval(() => {
      void mutateProgress();
    }, ENTITIES_PROGRESS_POLL_MS);
    return () => clearInterval(timer);
  }, [matterGuid, mutateProgress]);

  const previousStampRef = useRef<string | null>(null);
  useEffect(() => {
    if (!stamp) return;
    const previous = previousStampRef.current;
    previousStampRef.current = stamp;
    if (previous && previous !== stamp) void mutate();
  }, [stamp, mutate]);

  return {
    entities: data?.entities || [],
    isLoading,
    isError: error,
    mutate,
  };
}

export interface EntityMention {
  id: string;
  name: string;
  document_guid: string;
  document_name: string;
  passage: string | null;
  segment_index: number | null;
}

interface EntityMentionsResponse {
  mentions: EntityMention[];
  truncated: boolean;
}

export function useEntityMentions(matterGuid: string | undefined, entityId: string | null) {
  const { data, error, isLoading, mutate } = useSWR<EntityMentionsResponse>(
    matterGuid && entityId ? `/entities/${matterGuid}/${entityId}/mentions` : null,
    async (url: string) => {
      const response = await backendClient.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: false,
    },
  );

  return {
    mentions: data?.mentions ?? [],
    truncated: data?.truncated ?? false,
    isLoading,
    isError: error,
    mutate,
  };
}
